import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { IMDMPolicyBody, INucleusPolicyElement, IAndroidManagementPolicy } from './types';
import api from '../../utils/httpClient';

export const fetchMdmPolicyData = createAsyncThunk<INucleusPolicyElement[]>(
  'mdmPolicy/data/fetchMdmPolicyData',
  async () => {
    const { data } = await api.get('/mdm/policies') as { data: IMDMPolicyBody }
    return data.Policies;
  }
);

export const fetchMdmPolicyDetailsData = createAsyncThunk<IAndroidManagementPolicy, string>(
  'mdmPolicy/data/fetchMdmPolicyDetailsData',
  async (policyId: string) => {
    const { data } = await api.get(`/mdm/policy_template/${policyId}/data`) as { data: IAndroidManagementPolicy }
    return data;
  }
);

interface SaveMdmPolicyDataArgs {
  policyId: string;
  payload: any;
}

export const saveMdmPolicyData = createAsyncThunk<IAndroidManagementPolicy, SaveMdmPolicyDataArgs>(
  'mdmPolicy/data/saveMdmPolicyData',
  async ({ policyId, payload }) => {
    const { data } = await api.put(`/mdm/policy_template/${policyId}`, payload)
    return data;
  }
);

export const createMdmPolicyData = createAsyncThunk<IAndroidManagementPolicy, string>(
  'mdmPolicy/data/createMdmPolicyData',
  async (policyTemplate: any) => {
    const { data } = await api.post("/mdm/policy_template", policyTemplate)
    return data;
  }
);

export const deleteMdmPolicyData = createAsyncThunk<IAndroidManagementPolicy, string>(
  'mdmPolicy/data/deleteMdmPolicyData',
  async (policyId: string) => {
    const { data } = await api.delete(`/mdm/policy_template/${policyId}`)
    return data;
  }
)
export const fetchEnterprisesValues = createAsyncThunk<[{ ID: string, OrganizationName: string }]>(
  'mdmPolicy/data/fetchEnterprisesValues',
  async () => {
    const { data } = await api.get("/mdm/enterprises")
    return data.Enterprises as [{ ID: string, OrganizationName: string }];
  }
);

export const createEnterprise = createAsyncThunk<[{ OrganizationID: string, OrganizationName: string }]>(
  'mdmPolicy/data/createEnterprise',
  async (enterpriseData: any) => {
    const { data } = await api.post("/mdm/enterprises", enterpriseData);
    return data;
  }
);
interface updateEnterpriseArgs {
  enterpriseId: string;
  payload: any;
}
export const updateEnterprise = createAsyncThunk<IAndroidManagementPolicy, updateEnterpriseArgs>(
  'mdmPolicy/data/updateEnterprise',
  async ({ enterpriseId, payload }) => {
    const { data } = await api.put(`/mdm/enterprises/${enterpriseId}`, payload);
    return data;
  }
);

// Slice
const mdmPolicyDataSlice = createSlice({
  name: "mdmPolicy/data",
  initialState: {
    Policies: [] as INucleusPolicyElement[],
    PolicyDetails: {} as IAndroidManagementPolicy,
    loadingPolicyDetails: false,
    errorPolicyDetails: false,
    successPolicyDetails: false,
    savePolicyResponse: {},
    saveEnterpriseResponse: {},
    loading: false,
    success: false,
    error: false,
    errorMessage: null as string | null,
    loadingEnterprises: true,
    successEnterprises: false,
    errorEnterprises: false,
    errorMessageEnterprises: null as string | null,
    enterprisesOptions: [] as unknown as [{ ID: string, OrganizationName: string }],
    deleteMdmPolicyState: {
      loading: false,
      success: false,
      error: false,
      data: null
    } as {
      loading: boolean, success: boolean, error: boolean, data: null | unknown
    },
    // success
    create_success: false,
    update_success: false,
  },
  reducers: {
    resetFetchMdmPolicyDetailsDataState: (state : {loadingPolicyDetails: boolean; successPolicyDetails: boolean; errorPolicyDetails: boolean; errorMessage: string | null; PolicyDetails: IAndroidManagementPolicy}) => {
      state.loadingPolicyDetails = false;
      state.successPolicyDetails = false;
      state.errorPolicyDetails = false;
      state.errorMessage = null;
      state.PolicyDetails = {} as IAndroidManagementPolicy;
    },
    resetSuccessFormState: (state: { create_success: boolean; update_success: boolean; }) => {
      state.create_success = false;
      state.update_success = false;
    },
    resetDeleteMdmPolicyState: (state: { deleteMdmPolicyState: { loading: boolean; success: boolean; error: boolean; data: null | unknown; }; }) => {
      state.deleteMdmPolicyState = {
        loading: false,
        success: false,
        error: false,
        data: null
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMdmPolicyData.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = false;
        state.errorMessage = null;
      })
      .addCase(fetchMdmPolicyData.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = false;
        state.Policies = action.payload;
      })

      .addCase(fetchMdmPolicyData.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error = true;
        state.errorMessage = action.error ? action.error.message! : '';
      })
      .addCase(fetchMdmPolicyDetailsData.pending, (state) => {
        state.loadingPolicyDetails = true;
        state.successPolicyDetails = false;
        state.errorPolicyDetails = false;
        state.errorMessage = null;
      })
      .addCase(fetchMdmPolicyDetailsData.fulfilled, (state, action) => {
        state.loadingPolicyDetails = false;
        state.successPolicyDetails = true;
        state.errorPolicyDetails = false;
        state.PolicyDetails = action.payload;
      })
      .addCase(fetchMdmPolicyDetailsData.rejected, (state, action) => {
        state.loadingPolicyDetails = false;
        state.successPolicyDetails = false;
        state.errorPolicyDetails = true;
        state.errorMessage = action.error ? action.error.message! : '';
      })
      .addCase(saveMdmPolicyData.pending, (state) => {
        state.loading = true;
        state.update_success = false;
        state.error = false;
        state.errorMessage = null;
      })
      .addCase(saveMdmPolicyData.fulfilled, (state, action) => {
        state.loading = false;
        state.update_success = true;
        state.error = false;
        state.savePolicyResponse = action.payload;
      })

      .addCase(saveMdmPolicyData.rejected, (state, action) => {
        state.loading = false;
        state.update_success = false;
        state.error = true;
        state.errorMessage = action.error ? action.error.message! : '';
      })
      .addCase(createMdmPolicyData.pending, (state) => {
        state.loading = true;
        state.create_success = false;
        state.error = false;
        state.errorMessage = null;
      })
      .addCase(createMdmPolicyData.fulfilled, (state, action) => {
        state.loading = false;
        state.create_success = true;
        state.error = false;
        state.savePolicyResponse = action.payload;
      })

      .addCase(createMdmPolicyData.rejected, (state, action) => {
        state.loading = false;
        state.create_success = false;
        state.error = true;
        state.errorMessage = action.error ? action.error.message! : '';
      })
      .addCase(fetchEnterprisesValues.pending, (state) => {
        state.loadingEnterprises = true;
        state.successEnterprises = false;
        state.errorEnterprises = false;
        state.errorMessageEnterprises = null;
      })
      .addCase(fetchEnterprisesValues.fulfilled, (state, action) => {
        state.loadingEnterprises = false;
        state.successEnterprises = true;
        state.errorEnterprises = false;
        state.errorMessageEnterprises = null;
        state.enterprisesOptions = action.payload;
      })

      .addCase(fetchEnterprisesValues.rejected, (state, action) => {
        state.loadingEnterprises = false;
        state.successEnterprises = false;
        state.errorEnterprises = true;
        state.errorMessage = action.error ? action.error.message! : '';
      })
      .addCase(createEnterprise.pending, (state) => {
        state.loadingEnterprises = true;
        state.successEnterprises = false;
        state.errorEnterprises = false;
        state.errorMessageEnterprises = null;
      })
      .addCase(createEnterprise.fulfilled, (state, action) => {
        state.loadingEnterprises = false;
        state.successEnterprises = true;
        state.errorEnterprises = false;
        state.errorMessageEnterprises = null;
        state.saveEnterpriseResponse = action.payload;
      })

      .addCase(createEnterprise.rejected, (state, action) => {
        state.loadingEnterprises = false;
        state.successEnterprises = false;
        state.errorEnterprises = true;
        state.errorMessage = action.error ? action.error.message! : '';
      })
      .addCase(updateEnterprise.pending, (state) => {
        state.loadingEnterprises = true;
        state.successEnterprises = false;
        state.errorEnterprises = false;
        state.errorMessageEnterprises = null;
      })
      .addCase(updateEnterprise.fulfilled, (state, action) => {
        state.loadingEnterprises = false;
        state.successEnterprises = true;
        state.errorEnterprises = false;
        state.errorMessageEnterprises = null;
        state.saveEnterpriseResponse = action.payload;
      })

      .addCase(updateEnterprise.rejected, (state, action) => {
        state.loadingEnterprises = false;
        state.successEnterprises = false;
        state.errorEnterprises = true;
        state.errorMessage = action.error ? action.error.message! : '';
      })
      .addCase(deleteMdmPolicyData.pending, (state) => {
        state.deleteMdmPolicyState = {
          loading: true,
          success: false,
          error: false,
          data: null
        }
      })
      .addCase(deleteMdmPolicyData.fulfilled, (state, action) => {
        state.deleteMdmPolicyState = {
          loading: false,
          success: true,
          error: false,
          data: action.payload
        }
      })

      .addCase(deleteMdmPolicyData.rejected, (state) => {
        state.deleteMdmPolicyState = {
          loading: false,
          success: false,
          error: true,
          data: null
        }
      });
  },
});
export const {
  resetFetchMdmPolicyDetailsDataState,
  resetSuccessFormState,
  resetDeleteMdmPolicyState
} = mdmPolicyDataSlice.actions

export default mdmPolicyDataSlice.reducer;
